(() => {
  if (window.__veliaProductImageNormalizerLoaded) return;
  window.__veliaProductImageNormalizerLoaded = true;

  const imageSelector = [
    'hdt-card-product .hdt-card-product__media--main',
    'hdt-card-product .hdt-card-product__media--hover'
  ].join(',');
  const cache = new Map();
  const queued = new WeakSet();
  const compactViewport = window.matchMedia('(max-width: 767px)');

  const style = document.createElement('style');
  style.id = 'velia-product-image-normalizer-styles';
  style.textContent = `
    hdt-card-product .hdt-card-product__media,
    hdt-card-product .hdt-card-product__media-wrapp {
      background: #fff !important;
    }

    hdt-card-product .hdt-card-product__media {
      overflow: hidden;
    }

    hdt-card-product .hdt-card-product__media-wrapp {
      --aspect-ratioapt: 1 !important;
      aspect-ratio: 1 / 1;
    }

    hdt-card-product .hdt-card-product__media--main,
    hdt-card-product .hdt-card-product__media--hover {
      width: 100% !important;
      height: 100% !important;
      object-fit: contain !important;
      object-position: center !important;
      transform: scale(var(--velia-product-image-scale, 1)) !important;
      transform-origin: center !important;
    }

    hdt-card-product,
    hdt-card-product .hdt-card-product__wrapper,
    hdt-card-product .hdt-card-product__content,
    hdt-card-product .hdt-card-product__title,
    hdt-card-product .hdt-card-product__title a {
      min-width: 0;
      max-width: 100%;
    }

    hdt-card-product .hdt-card-product__title a {
      display: block;
      overflow-wrap: anywhere;
      word-break: break-word;
      hyphens: auto;
    }
  `;
  document.head.appendChild(style);

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function backgroundAverage(pixels, size) {
    const sample = 8;
    const corners = [
      [0, 0],
      [size - sample, 0],
      [0, size - sample],
      [size - sample, size - sample]
    ];
    let red = 0;
    let green = 0;
    let blue = 0;
    let count = 0;

    corners.forEach(([startX, startY]) => {
      for (let y = startY; y < startY + sample; y += 1) {
        for (let x = startX; x < startX + sample; x += 1) {
          const index = (y * size + x) * 4;
          if (pixels[index + 3] < 24) continue;
          red += pixels[index];
          green += pixels[index + 1];
          blue += pixels[index + 2];
          count += 1;
        }
      }
    });

    if (!count) return [255, 255, 255];
    return [red / count, green / count, blue / count];
  }

  function calculateScale(image) {
    const size = 180;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return 1;

    canvas.width = size;
    canvas.height = size;
    context.fillStyle = '#fff';
    context.fillRect(0, 0, size, size);

    try {
      context.drawImage(image, 0, 0, size, size);
      const data = context.getImageData(0, 0, size, size).data;
      const [backgroundRed, backgroundGreen, backgroundBlue] = backgroundAverage(data, size);
      const threshold = 24;
      const rowCounts = new Uint16Array(size);
      const columnCounts = new Uint16Array(size);
      let foregroundPixels = 0;

      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          const index = (y * size + x) * 4;
          if (data[index + 3] < 24) continue;
          const difference = Math.max(
            Math.abs(data[index] - backgroundRed),
            Math.abs(data[index + 1] - backgroundGreen),
            Math.abs(data[index + 2] - backgroundBlue)
          );
          if (difference <= threshold) continue;

          rowCounts[y] += 1;
          columnCounts[x] += 1;
          foregroundPixels += 1;
        }
      }

      if (foregroundPixels < 120) return 1;

      // Ignore isolated JPEG/background noise at the outer edges. A row or
      // column must contain several foreground pixels to belong to the product.
      const minimumLinePixels = 4;
      const minX = columnCounts.findIndex((count) => count >= minimumLinePixels);
      const minY = rowCounts.findIndex((count) => count >= minimumLinePixels);
      let maxX = -1;
      let maxY = -1;

      for (let index = size - 1; index >= 0; index -= 1) {
        if (maxX < 0 && columnCounts[index] >= minimumLinePixels) maxX = index;
        if (maxY < 0 && rowCounts[index] >= minimumLinePixels) maxY = index;
        if (maxX >= 0 && maxY >= 0) break;
      }

      if (minX < 0 || minY < 0 || maxX < minX || maxY < minY) return 1;

      const widthRatio = (maxX - minX + 1) / size;
      const heightRatio = (maxY - minY + 1) / size;

      // Full-bleed/lifestyle images are deliberately left untouched.
      if (widthRatio > 0.94 && heightRatio > 0.94) return 1;

      // Only visually oversized cut-outs are reduced. Smaller products are
      // never enlarged, so source quality and the intended hierarchy remain intact.
      const targetWidth = 0.72;
      const targetHeight = 0.70;
      return {
        scale: clamp(Math.min(1, targetWidth / widthRatio, targetHeight / heightRatio), 0.78, 1),
        widthRatio,
        heightRatio
      };
    } catch (error) {
      return 1;
    }
  }

  function applyScale(image, measurement) {
    const scale = typeof measurement === 'number' ? measurement : measurement.scale;
    // The two-column mobile grid has much narrower cards. Reduce the complete
    // cut-out once more there so the product has enough white space and the
    // text area remains visually separate from the neighbouring card.
    const viewportFactor = compactViewport.matches ? 0.82 : 1;
    image.style.setProperty('--velia-product-image-scale', (scale * viewportFactor).toFixed(3));
    image.dataset.veliaImageBaseScale = scale.toFixed(3);
    if (typeof measurement !== 'number') {
      image.dataset.veliaImageWidthRatio = measurement.widthRatio.toFixed(3);
      image.dataset.veliaImageHeightRatio = measurement.heightRatio.toFixed(3);
    }
    image.dataset.veliaImageNormalized = 'true';
  }

  function normalize(image) {
    if (!(image instanceof HTMLImageElement) || queued.has(image)) return;
    queued.add(image);

    const run = () => {
      queued.delete(image);
      const source = image.currentSrc || image.src;
      if (!source) return;

      if (cache.has(source)) {
        applyScale(image, cache.get(source));
        return;
      }

      const measure = () => {
        const scale = calculateScale(image);
        cache.set(source, scale);
        applyScale(image, scale);
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(measure, { timeout: 700 });
      } else {
        window.setTimeout(measure, 0);
      }
    };

    if (image.complete && image.naturalWidth > 0) {
      run();
    } else {
      image.addEventListener('load', run, { once: true });
    }
  }

  function scan(root = document) {
    if (root.matches?.(imageSelector)) normalize(root);
    root.querySelectorAll?.(imageSelector).forEach(normalize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  const refreshViewportScale = () => {
    document.querySelectorAll(imageSelector).forEach((image) => {
      const source = image.currentSrc || image.src;
      if (source && cache.has(source)) applyScale(image, cache.get(source));
    });
  };

  if (compactViewport.addEventListener) {
    compactViewport.addEventListener('change', refreshViewportScale);
  } else {
    compactViewport.addListener(refreshViewportScale);
  }
})();
